import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import {
  HOTEL_SEARCH_TYPEAHEAD_TYPES,
  LOCATION_SEARCH_ERROR_MESSAGE,
  NATURAL_LANGUAGE_SEARCH_DEFAULT_SUGGESTIONS,
} from 'constants/search'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'LuxKit/Typography/BodyText'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import { usePlacesBySearchPhrase } from 'hooks/usePlace'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import styled from 'styled-components'
import { take } from 'lib/array/arrayUtils'
import clsx from 'clsx'
import ListItem from 'LuxKit/List/ListItem'
import ListSubheader from 'LuxKit/List/ListSubheader'
import { connect } from 'react-redux'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { pluralizeToString } from 'lib/string/pluralize'
import { TypeaheadActionType } from 'analytics/snowplow/events'
import SearchTextListItem from '../SearchTextListItem/SearchTextListItem'
import VerticalSpacer from '../../Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from '../../utils/breakpoint'
import { createNaturalLanguageItem } from 'lib/search/searchUtils'

const ResultContainer = styled.div`
  position: relative;
  min-height: ${rem(80)};
`

const Separator = styled.div`
  margin-top: ${rem(24)};
`

const ItemContainer = styled.div`
  &.horizontal {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  }

  &.useImage {
    display: flex;
    column-gap: ${rem(40)};
    flex-wrap: wrap;
  }
`

const NaturalLanguageSearchDefaultSuggestions = styled.div`
  padding-top: ${rem(16)};
  ${mediaQueryUp.tablet} {
    padding-top: 0;
  }
`
interface MappedProps {
  canViewLuxPlusBenefits: boolean
}

interface Props extends MappedProps {
  className?: string
  searchPhrase?: string
  searchTypes?: Array<App.SearchType>
  placeTypes?: Array<App.SearchPlaceType>
  onChange?: (
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
    actionType?: TypeaheadActionType,
  ) => void
  onResultsChange?: (results: Array<App.SearchItem>, searchPhrase: string, shouldTrack?: boolean, loading?: boolean) => void
  vertical?: App.SearchPlaceVertical
  overrideSearchItems?: Array<App.SearchItem>
  selectedDropdown?: number
  showHorizontalLayout?: boolean
  useImage?: boolean
  showNaturalLanguageSearchSuggestions?: boolean
}

function SearchPlaceSelectList(props: Props) {
  const {
    className,
    searchPhrase = '',
    onChange = noop,
    placeTypes,
    searchTypes,
    onResultsChange = noop,
    overrideSearchItems,
    vertical,
    selectedDropdown,
    showHorizontalLayout = false,
    canViewLuxPlusBenefits,
    useImage,
    showNaturalLanguageSearchSuggestions = false,
  }: Props = props

  const isHotelSearch = placeTypes == HOTEL_SEARCH_TYPEAHEAD_TYPES
  const priority = isHotelSearch ? 'next' : 'current'
  const limit = vertical === 'tour' ? 10 : 9
  const [searchResults, loading] = usePlacesBySearchPhrase(searchPhrase, {
    disabled: !!overrideSearchItems,
    placeTypes,
    searchTypes,
    vertical,
    priority,
    limit,
    canViewLuxPlusBenefits,
  })

  const tourResults = useMemo(() => {
    if (!overrideSearchItems) {
      return searchResults.filter(result => result.searchType === 'tour')
    }
    return overrideSearchItems.filter(result => result.searchType === 'tour')
  }, [overrideSearchItems, searchResults])

  const searchResultAmount = useImage ? 9 : (vertical === 'tour' || showHorizontalLayout) ? 6 : 8

  const finalResults = useMemo<Array<App.SearchItem>>(() => {
    if (!overrideSearchItems) {
      const results = take(searchResults.filter(result => result.searchType !== 'tour'), searchResultAmount)

      if (showNaturalLanguageSearchSuggestions) {
        return [
          createNaturalLanguageItem(searchPhrase),
          ...take(NATURAL_LANGUAGE_SEARCH_DEFAULT_SUGGESTIONS, 2).map(createNaturalLanguageItem),
          ...results
        ]
      }
      return results
    }
    return overrideSearchItems.filter(result => {
      const normalisedSearch = searchPhrase.toLowerCase()
      return result.format.mainText.toLowerCase().includes(normalisedSearch) ||
        result.format.secondaryText?.toLowerCase().includes(normalisedSearch)
    })
  }, [overrideSearchItems, searchResultAmount, searchResults, searchPhrase, showNaturalLanguageSearchSuggestions])

  // Keep previous results visible while loading new ones
  const [localFinalResults, setLocalFinalResults] = useState<Array<App.SearchItem>>([])
  const [localTourResults, setLocalTourResults] = useState<Array<App.SearchItem>>([])

  useEffect(() => {
    setLocalFinalResults(finalResults)
    setLocalTourResults(tourResults)
  }, [finalResults, tourResults])

  // Track when loading completes to avoid duplicate analytics
  const prevLoadingRef = useRef(loading)

  useEffect(() => {
    const wasLoading = prevLoadingRef.current
    const loadingCompleted = wasLoading && !loading

    // Only track analytics when loading transitions from true to false
    onResultsChange(finalResults, searchPhrase, loadingCompleted, loading)

    prevLoadingRef.current = loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalResults, loading])

  const handleOnClick = useCallback((searchItem?: App.SearchItem) => {
    if (!searchItem) {
      onChange(searchItem)
      return
    }

    const itemIndex = localFinalResults.findIndex(item => item.value === searchItem.value)
    if (itemIndex === -1) {
      onChange(searchItem)
      return
    }

    onChange(searchItem, undefined, undefined)
  }, [localFinalResults, onChange])

  const naturalLanguageResults = useMemo(() => {
    return localFinalResults.filter(result => result.searchType === 'naturalLanguage')
  }, [localFinalResults])

  const nonNaturalLanguageResults = useMemo(() => {
    return localFinalResults.filter(result => result.searchType !== 'naturalLanguage')
  }, [localFinalResults])

  const firstNaturalLanguageResult = useMemo(() => {
    if (showNaturalLanguageSearchSuggestions && searchPhrase) {
      return createNaturalLanguageItem(searchPhrase)
    }
    return undefined
  }, [showNaturalLanguageSearchSuggestions, searchPhrase])

  const isInitialLoad = loading && localFinalResults.length === 0

  return <ResultContainer className={className}>
    {isInitialLoad && !showNaturalLanguageSearchSuggestions && (
      <LoadingIndicator visible={loading} opaque floating>
        <BodyText variant="large" weight="bold" colour="neutral-one">Loading</BodyText>
      </LoadingIndicator>
    )}
    <VerticalSpacer gap={24}>

      {showNaturalLanguageSearchSuggestions && firstNaturalLanguageResult && (
        <NaturalLanguageSearchDefaultSuggestions>
          <SearchTextListItem
            text={firstNaturalLanguageResult.value}
            isHighlighted
            onClick={handleOnClick}
          />
          {naturalLanguageResults.slice(1).map((suggestion, index) =>
            <SearchTextListItem
              key={`${suggestion.value}_${index}`}
              text={suggestion.value}
              boldText={searchPhrase}
              onClick={handleOnClick}
            />)}
        </NaturalLanguageSearchDefaultSuggestions>
      )}

      {!isInitialLoad && (
        <>
          {!showNaturalLanguageSearchSuggestions && (
            <>
              {searchPhrase.length > 0 && localFinalResults.length === 0 && localTourResults.length === 0 && <ListItem
                title={<>Sorry, we couldn't find <b>{searchPhrase}</b>.</>}
                subtitle={LOCATION_SEARCH_ERROR_MESSAGE}
              />}
              {searchPhrase.length === 0 && localFinalResults.length === 0 && localTourResults.length === 0 && <ListItem
                title={<>Sorry, we couldn't find a match.</>}
                subtitle={LOCATION_SEARCH_ERROR_MESSAGE}
              />}
            </>
          )}
          {searchPhrase.length > 0 && <ItemContainer className={clsx({ horizontal: showHorizontalLayout, useImage })}>
            {nonNaturalLanguageResults.map((searchItem, index) => <SearchMenuListItem
              key={`${searchItem.value}_${index}`}
              searchItem={searchItem}
              onClick={handleOnClick}
              searchPhrase={searchPhrase}
              selected={index === selectedDropdown}
              horizontal={showHorizontalLayout}
              searchVertical={vertical}
              imageId={searchItem.format.imageId}
              useImage={useImage}
            />)}
          </ItemContainer>}
        </>
      )}
    </VerticalSpacer>

    {!isInitialLoad && localTourResults.length > 0 && <Separator>
      <ListSubheader>
        Related {pluralizeToString('tour', localTourResults.length)}
      </ListSubheader>
      <ItemContainer className={clsx({ horizontal: showHorizontalLayout })}>
        {localTourResults.map((searchItem, index) => <SearchMenuListItem
          key={`${searchItem.value}_${index}`}
          searchItem={searchItem}
          onClick={handleOnClick}
          searchPhrase={searchPhrase}
        />)}
      </ItemContainer>
    </Separator>}
  </ResultContainer>
}
const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
})

export default connect(mapStateToProps)(SearchPlaceSelectList)
